import {
  CONTACT_FORM_TYPES,
  ContactForm,
  type ContactFormType,
} from '@/app/settings/_components/contact-form'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from '@/components/ui/toaster'
import { VisuallyHidden } from '@/components/ui/visually-hidden'
import { T, type TranslationKey, useTranslate } from '@tolgee/react'
import React from 'react'

export function ContactDialog({ children }: Readonly<{ children: React.ReactNode }>) {
  const { t } = useTranslate()
  const [open, setOpen] = React.useState(false)
  const [formType, setFormType] = React.useState<ContactFormType>(DEFAULT_FORM_TYPE)

  const onSubmitSuccess = () => {
    setOpen(false)
    toast.success(t('account.contact.toasts.success'))
    setFormType(DEFAULT_FORM_TYPE)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogPortal>
        <DialogContent>
          <div>
            <DialogTitle className='mb-4'>
              <Label htmlFor='formType' variant='dialog-title'>
                <T keyName='account.contact.dialog.title' />
              </Label>
            </DialogTitle>
            <VisuallyHidden asChild>
              <DialogDescription>
                <T keyName='account.contact.dialog.description' />
              </DialogDescription>
            </VisuallyHidden>
            <Select
              name='formType'
              value={formType}
              onValueChange={(value) => setFormType(value as ContactFormType)}
            >
              <SelectTrigger isDirty className='max-w-60'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTACT_FORM_TYPES.map((key) => (
                  <SelectItem key={key} value={key}>
                    <T keyName={FORM_TYPE_DISPLAY_KEYS[key]} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <ContactForm
            onSubmitSuccess={onSubmitSuccess}
            contactType={formType}
            renderFooter={(submitButton) => (
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant='mango-outline' title={t('shared.cancelDialog.a11y')}>
                    <T keyName='shared.cancelDialog.title' />
                  </Button>
                </DialogClose>
                {submitButton}
              </DialogFooter>
            )}
          />
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

// 👇 static data

const DEFAULT_FORM_TYPE: ContactFormType = 'HELP'

const FORM_TYPE_DISPLAY_KEYS: Record<ContactFormType, TranslationKey> = {
  HELP: 'account.contact.dialog.typeOptions.help.value',
  FEEDBACK: 'account.contact.dialog.typeOptions.feedback.value',
  BUGS: 'account.contact.dialog.typeOptions.bugs.value',
}
